import { useState, useEffect } from 'react';
import { adminAPI } from '../lib/api';

export default function SecurityEvents() {
  const [activeTab, setActiveTab] = useState('events');
  const [events, setEvents] = useState([]);
  const [failedLogins, setFailedLogins] = useState([]);
  const [lockouts, setLockouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (activeTab === 'events') {
        const response = await adminAPI.getSecurityEvents();
        setEvents(response.data?.events || response.data || []);
      } else if (activeTab === 'failed-logins') {
        const response = await adminAPI.getFailedLogins();
        setFailedLogins(response.data?.failed_logins || response.data || []);
      } else if (activeTab === 'lockouts') {
        const response = await adminAPI.getLockouts();
        setLockouts(response.data?.lockouts || response.data || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResolveEvent = async (eventId) => {
    if (!confirm('Are you sure you want to resolve this security event?')) {
      return;
    }

    try {
      await adminAPI.resolveSecurityEvent(eventId);
      loadData();
    } catch (err) {
      alert('Failed to resolve event: ' + err.message);
    }
  };

  const handleUnlockAccount = async (lockoutId) => {
    try {
      await adminAPI.unlockAccount(lockoutId);
      loadData();
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message;
      alert('Failed to unlock account: ' + errorMessage);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Security Monitoring</h1>
        <button
          onClick={loadData}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <TabButton
            active={activeTab === 'events'}
            onClick={() => setActiveTab('events')}
            label="Security Events"
          />
          <TabButton
            active={activeTab === 'failed-logins'}
            onClick={() => setActiveTab('failed-logins')}
            label="Failed Logins"
          />
          <TabButton
            active={activeTab === 'lockouts'}
            onClick={() => setActiveTab('lockouts')}
            label="Account Lockouts"
          />
        </nav>
      </div>

      {/* Content */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error: {error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {activeTab === 'events' && (
            <SecurityEventsTable events={events} onResolve={handleResolveEvent} />
          )}
          {activeTab === 'failed-logins' && (
            <FailedLoginsTable logins={failedLogins} />
          )}
          {activeTab === 'lockouts' && (
            <LockoutsTable lockouts={lockouts} onUnlock={handleUnlockAccount} />
          )}
        </>
      )}
    </div>
  );
}

function TabButton({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      className={`
        py-4 px-1 border-b-2 font-medium text-sm
        ${active
          ? 'border-blue-500 text-blue-600'
          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
        }
      `}
    >
      {label}
    </button>
  );
}

function SecurityEventsTable({ events, onResolve }) {
  const eventArray = Array.isArray(events) ? events : [];

  if (eventArray.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <p className="text-gray-500">No security events found</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Event Type
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Severity
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              User
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              IP Address
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Time
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {eventArray.map((event) => (
            <tr key={event.id}>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {event.event_type}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <SeverityBadge severity={event.severity} />
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {event.user_id || 'N/A'}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {event.ip_address}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {new Date(event.created_at).toLocaleString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {event.resolved_at ? (
                  <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                    Resolved
                  </span>
                ) : (
                  <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">
                    Open
                  </span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm">
                {!event.resolved_at && (
                  <button
                    onClick={() => onResolve(event.id)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    Resolve
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FailedLoginsTable({ logins }) {
  const loginArray = Array.isArray(logins) ? logins : [];

  if (loginArray.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <p className="text-gray-500">No failed login attempts found</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Email
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              IP Address
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Reason
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Time
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              User Agent
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {loginArray.map((login) => (
            <tr key={login.id}>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {login.email}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {login.ip_address}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {login.reason}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {new Date(login.attempt_time).toLocaleString()}
              </td>
              <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                {login.user_agent}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LockoutsTable({ lockouts, onUnlock }) {
  const lockoutArray = Array.isArray(lockouts) ? lockouts : [];

  if (lockoutArray.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <p className="text-gray-500">No account lockouts found</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Email
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Reason
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Failed Attempts
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Locked Until
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {lockoutArray.map((lockout) => (
            <tr key={lockout.id}>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {lockout.email}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {lockout.reason}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {lockout.failed_attempt_count}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {new Date(lockout.locked_until).toLocaleString()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {lockout.unlocked_at ? (
                  <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                    Unlocked
                  </span>
                ) : new Date(lockout.locked_until) > new Date() ? (
                  <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                    Active
                  </span>
                ) : (
                  <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
                    Expired
                  </span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm">
                {!lockout.unlocked_at && new Date(lockout.locked_until) > new Date() && (
                  <button
                    onClick={() => onUnlock(lockout.id)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    Unlock
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SeverityBadge({ severity }) {
  const colors = {
    low: 'bg-blue-100 text-blue-800',
    medium: 'bg-yellow-100 text-yellow-800',
    high: 'bg-orange-100 text-orange-800',
    critical: 'bg-red-100 text-red-800',
  };

  return (
    <span className={`px-2 py-1 text-xs rounded-full ${colors[severity] || colors.low}`}>
      {severity}
    </span>
  );
}
